// src/hooks/useNotifications.js
import { useState, useEffect, useCallback } from "react";
import {
  collection, query, orderBy, limit, onSnapshot,
  addDoc, updateDoc, doc, writeBatch, serverTimestamp, where
} from "firebase/firestore";
import { db } from "../lib/firebase";

// ── Types de notifications ─────────────────────────────────────────────
export const NOTIF_TYPES = {
  DUEL_CHALLENGE:   { icon:"⚔️",  label:"Défi en duel",          color:"var(--blood)"  },
  DUEL_RESULT_WIN:  { icon:"🏆",  label:"Duel gagné",             color:"#4a9e6b"       },
  DUEL_RESULT_LOSS: { icon:"💀",  label:"Duel perdu",             color:"var(--ash)"    },
  TEAM_INVITE:      { icon:"🔴",  label:"Invitation en team",     color:"var(--ember)"  },
  TEAM_BATTLE_START:{ icon:"⚡",  label:"Battle démarrée",        color:"var(--blood)"  },
  TEAM_BATTLE_END:  { icon:"🎖️", label:"Battle terminée",        color:"var(--gold)"   },
  LEVEL_UP:         { icon:"📈",  label:"Level up !",             color:"var(--gold)"   },
  BADGE_UNLOCKED:   { icon:"🎖️", label:"Badge débloqué",         color:"var(--gold)"   },
  REFORM_AVAILABLE: { icon:"🔥",  label:"Oni Reform disponible",  color:"var(--blood)"  },
  HOROSCOPE:        { icon:"👁️", label:"Horoscope de la semaine", color:"var(--ash)"    },
  DUEL_YOUR_TURN:   { icon:"⚔️",  label:"C'est ton tour de dueler", color:"var(--blood)"},
};

// ── Send notification (callable depuis n'importe où) ───────────────────
export async function sendNotification(toUid, { type, title, body, link, fromName, fromEmoji }) {
  if (!toUid || !type) return;
  try {
    await addDoc(collection(db, "users", toUid, "notifications"), {
      type, title, body, link,
      fromName: fromName || null,
      fromEmoji: fromEmoji || null,
      read: false,
      createdAt: serverTimestamp(),
    });
  } catch(e) {
    console.error("[Notif] Erreur envoi:", e.message);
  }
}

// ── Hook principal ─────────────────────────────────────────────────────
export function useNotifications(uid) {
  const [notifs, setNotifs]     = useState([]);
  const [unreadCount, setUnread] = useState(0);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    if (!uid) { setLoading(false); return; }

    const q = query(
      collection(db, "users", uid, "notifications"),
      orderBy("createdAt", "desc"),
      limit(30)
    );

    const unsub = onSnapshot(q, snap => {
      const data = snap.docs.map(d => ({
        id: d.id, ...d.data(),
        createdAt: d.data().createdAt?.toDate?.() || new Date(),
      }));
      setNotifs(data);
      setUnread(data.filter(n => !n.read).length);
      setLoading(false);
    });

    return unsub;
  }, [uid]);

  const markRead = useCallback(async (notifId) => {
    if (!uid) return;
    await updateDoc(doc(db, "users", uid, "notifications", notifId), {
      read: true, readAt: serverTimestamp(),
    });
  }, [uid]);

  const markAllRead = useCallback(async () => {
    if (!uid) return;
    const unread = notifs.filter(n => !n.read);
    if (!unread.length) return;
    const batch = writeBatch(db);
    unread.forEach(n => {
      batch.update(doc(db, "users", uid, "notifications", n.id), {
        read: true, readAt: serverTimestamp(),
      });
    });
    await batch.commit();
  }, [uid, notifs]);

  return { notifs, unreadCount, loading, markRead, markAllRead };
}
